/**
 * AdminFinancials Component
 *
 * Top-level container for the Beu admin financials dashboard.
 * Mirrors the tabbed pattern used by the salon Finance.js view
 * (URL ?tab= query param + activeTab state).
 *
 * Tabs: Overview | Processing | Off-platform | Subscriptions | Reconciliation | KPIs
 *
 * A single date-range picker at the top controls the period every tab queries
 * against. Reuses the rich picker from `finance/components/DateRangePicker`.
 */

import React, { Component } from 'react';
import AdminWrapper from './AdminWrapper';
import OverviewView from './financials/OverviewView';
import ProcessingView from './financials/ProcessingView';
import OffPlatformView from './financials/OffPlatformView';
import SalonsView from './financials/SalonsView';
import SubscriptionsView from './financials/SubscriptionsView';
import ReconciliationView from './financials/ReconciliationView';
import KpisView from './financials/KpisView';
import DateRangePicker from '../finance/components/DateRangePicker';
import { getDatePresetLabel } from '../finance/data/mockData';
import './Admin.css';
import './financials/AdminFinancials.css';

const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'salons', label: 'Salons' },
    { id: 'processing', label: 'Processing' },
    { id: 'off-platform', label: 'Off-platform' },
    { id: 'subscriptions', label: 'Subscriptions' },
    { id: 'reconciliation', label: 'Reconciliation' },
    { id: 'kpis', label: 'KPIs' }
];

// Compute date range from a preset id (mirrors finance/ReportsView.js)
function getDateRangeFromPreset(preset) {
    const now = new Date();
    let startDate, endDate;
    switch (preset) {
        case 'today':
            startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            endDate = new Date();
            break;
        case 'yesterday': {
            const y = new Date();
            y.setDate(y.getDate() - 1);
            startDate = new Date(y.getFullYear(), y.getMonth(), y.getDate());
            endDate = new Date(y.getFullYear(), y.getMonth(), y.getDate(), 23, 59, 59);
            break;
        }
        case 'last7':
            endDate = new Date();
            startDate = new Date(); startDate.setDate(startDate.getDate() - 7);
            break;
        case 'last4weeks':
            endDate = new Date();
            startDate = new Date(); startDate.setDate(startDate.getDate() - 28);
            break;
        case 'last6months':
            endDate = new Date();
            startDate = new Date(); startDate.setMonth(startDate.getMonth() - 6);
            break;
        case 'last12months':
            endDate = new Date();
            startDate = new Date(); startDate.setFullYear(startDate.getFullYear() - 1);
            break;
        case 'wtd':
            endDate = new Date();
            startDate = new Date(); startDate.setDate(startDate.getDate() - startDate.getDay());
            break;
        case 'qtd': {
            endDate = new Date();
            const q = Math.floor(now.getMonth() / 3);
            startDate = new Date(now.getFullYear(), q * 3, 1);
            break;
        }
        case 'ytd':
            endDate = new Date();
            startDate = new Date(now.getFullYear(), 0, 1);
            break;
        case 'all':
            endDate = new Date();
            startDate = new Date(2020, 0, 1);
            break;
        case 'mtd':
        default:
            endDate = new Date();
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    }
    return { startDate, endDate };
}

class AdminFinancials extends Component {
    constructor(props) {
        super(props);
        const params = new URLSearchParams(props.location?.search || '');
        const tabParam = params.get('tab');
        const initialTab = TABS.find(t => t.id === tabParam)?.id || 'overview';
        const initialPreset = 'mtd';
        const { startDate, endDate } = getDateRangeFromPreset(initialPreset);
        this.state = {
            activeTab: initialTab,
            datePickerOpen: false,
            datePreset: initialPreset,
            dateStart: startDate,
            dateEnd: endDate
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location?.search !== this.props.location?.search) {
            const params = new URLSearchParams(this.props.location?.search || '');
            const tabParam = params.get('tab');
            const next = TABS.find(t => t.id === tabParam)?.id || 'overview';
            if (next !== this.state.activeTab) {
                this.setState({ activeTab: next });
            }
        }
    }

    setActiveTab = (id) => {
        this.setState({ activeTab: id });
        const qs = id === 'overview' ? '' : `?tab=${id}`;
        this.props.history.replace(`/beu-admin-x7k9/financials${qs}`);
    };

    handlePresetChange = (presetId) => {
        // If a preset is picked, sync the date range to it. If the user is
        // typing a custom range, DateRangePicker calls onPresetChange(null)
        // so we should only update from a real preset id.
        if (!presetId) {
            this.setState({ datePreset: null });
            return;
        }
        const { startDate, endDate } = getDateRangeFromPreset(presetId);
        this.setState({ datePreset: presetId, dateStart: startDate, dateEnd: endDate });
    };

    handleDateApply = (start, end) => {
        this.setState({ dateStart: start, dateEnd: end, datePickerOpen: false });
    };

    renderActive() {
        const { dateStart, dateEnd } = this.state;
        const props = { startDate: dateStart, endDate: dateEnd };
        switch (this.state.activeTab) {
            case 'salons': return <SalonsView />;
            case 'processing': return <ProcessingView {...props} />;
            case 'off-platform': return <OffPlatformView {...props} />;
            case 'subscriptions': return <SubscriptionsView {...props} />;
            case 'reconciliation': return <ReconciliationView {...props} />;
            case 'kpis': return <KpisView {...props} />;
            case 'overview':
            default: return <OverviewView {...props} />;
        }
    }

    render() {
        const { activeTab, datePickerOpen, datePreset, dateStart, dateEnd } = this.state;
        const rangeLabel = datePreset
            ? getDatePresetLabel(datePreset)
            : `${dateStart?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${dateEnd?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;

        return (
            <AdminWrapper>
                <div className="admin-page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                    <div>
                        <h1 className="admin-page-title">Financials</h1>
                        <p className="admin-page-subtitle">Revenue, margins and unit economics across Beu</p>
                    </div>

                    <div style={{ position: 'relative' }}>
                        <button
                            className="financials-date-btn"
                            onClick={() => this.setState({ datePickerOpen: !datePickerOpen })}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 8, verticalAlign: 'middle' }}>
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            <span style={{ color: '#007EA0', fontWeight: 500 }}>{rangeLabel}</span>
                            <span style={{ marginLeft: 8, color: '#a0a0a0' }}>▾</span>
                        </button>
                        <DateRangePicker
                            isOpen={datePickerOpen}
                            onClose={() => this.setState({ datePickerOpen: false })}
                            startDate={dateStart}
                            endDate={dateEnd}
                            selectedPreset={datePreset}
                            onPresetChange={this.handlePresetChange}
                            onApply={this.handleDateApply}
                            align="right"
                            openDirection="down"
                        />
                    </div>
                </div>

                <div className="financials-tabs">
                    {TABS.map(t => (
                        <button
                            key={t.id}
                            className={`financials-tab-btn ${activeTab === t.id ? 'active' : ''}`}
                            onClick={() => this.setActiveTab(t.id)}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="financials-content">
                    {this.renderActive()}
                </div>
            </AdminWrapper>
        );
    }
}

export default AdminFinancials;
